import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuthenticatedFetchWithWorkspace } from '../Keycloak/useAuthenticatedFetchWithWorkspace';
import { useQueryBuilderCache } from '../../context/QueryBuilderCacheContext';
import useWorkspaceChange from '../useWorkspaceChange';

export interface AvailableGroup {
    id: number;
    userId: number;
    description: string;
    source: string;
    updateDate: string;
}

const GROUPS_CACHE_KEY = 'available_groups';

export const useAvailableGroups = () => {
    const { authFetch, isReady, workspaceId } = useAuthenticatedFetchWithWorkspace();
    const cache = useQueryBuilderCache();
    // Scoped by workspace: switching workspace changes this key, so groups from the previous
    // workspace are never served as a stale cache hit for the new one.
    const cacheKey = `${GROUPS_CACHE_KEY}:${workspaceId ?? 'none'}`;

    const [groups, setGroups] = useState<AvailableGroup[]>(() =>
        cache?.get<AvailableGroup[]>(cacheKey)?.data ?? []
    );
    const [loading, setLoading] = useState<boolean>(() =>
        !cache?.get<AvailableGroup[]>(cacheKey)
    );
    const [error, setError] = useState<string | null>(null);

    const fetchGroups = useCallback(async (forceRefresh = false) => {
        if (!isReady) return;
        if (!forceRefresh) {
            const hit = cache?.get<AvailableGroup[]>(cacheKey);
            if (hit) { setGroups(hit.data); setLoading(false); return; }
        }
        setLoading(true);
        setError(null);
        try {
            const response = await authFetch('/getAvailableGroupsQuery', { method: 'GET' }) as AvailableGroup[] | { member?: AvailableGroup[] } | { 'hydra:member'?: AvailableGroup[] };
            const groupsData: AvailableGroup[] = Array.isArray(response)
                ? response
                : (response as { member?: AvailableGroup[] }).member
                    ?? (response as { 'hydra:member'?: AvailableGroup[] })['hydra:member']
                    ?? [];
            setGroups(groupsData);
            cache?.set(cacheKey, groupsData);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur de chargement des groupes');
        } finally {
            setLoading(false);
        }
    }, [authFetch, isReady, cache, cacheKey]);

    // Clears the previous workspace's groups immediately on switch, instead of leaving them
    // visible until the refetch for the new workspace resolves.
    useWorkspaceChange(() => {
        setGroups([]);
        setLoading(true);
    });

    useEffect(() => {
        fetchGroups();
    }, [fetchGroups]);

    return useMemo(() => ({
        groups,
        loading,
        error,
        refetch: () => fetchGroups(true)
    }), [groups, loading, error, fetchGroups]);
};
